import { useEffect, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate } from "./api";

const inputCls = "w-full bg-white border border-military/20 rounded-lg px-3 py-2.5 text-sm text-military-dark focus:outline-none focus:border-military";
const typeLabel = { entrada: "Entrada", saida: "Saída", ajuste: "Ajuste", venda: "Venda" };
const typeColor = { entrada: "bg-military/10 text-military", saida: "bg-red-100 text-red-700", ajuste: "bg-baby text-military-dark", venda: "bg-military-dark text-offwhite" };

const statusOf = (p) =>
  p.stock === 0
    ? <span className="text-[10px] uppercase tracking-wider font-semibold bg-red-100 text-red-700 px-2.5 py-1 rounded-full">Esgotado</span>
    : p.stock <= p.min_stock
      ? <span className="text-[10px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">Baixo</span>
      : <span className="text-[10px] uppercase tracking-wider font-semibold bg-military/10 text-military px-2.5 py-1 rounded-full">OK</span>;

export default function Stock() {
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [modal, setModal] = useState(null); // {type, product}
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");

  const load = () => {
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/stock/movements").then((r) => setMovements(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/stock/movements", { slug: modal.product.slug, type: modal.type, qty: Number(qty), reason });
      toast.success("Movimentação registrada");
      setModal(null);
      setQty("");
      setReason("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="admin-stock">
      <h1 className="font-serif text-3xl md:text-4xl text-military-dark mb-8">Estoque</h1>

      <div className="space-y-3 mb-14">
        {products.map((p) => (
          <div key={p.slug} data-testid={`stock-row-${p.slug}`} className="bg-white border border-military/10 rounded-lg p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[160px]">
              <p className="font-semibold text-military-dark text-sm">{p.name}</p>
              <p className="text-xs text-military-dark/50">mínimo: {p.min_stock}</p>
            </div>
            <p className={`font-serif text-3xl ${p.stock === 0 ? "text-red-700" : "text-military-dark"}`} data-testid={`stock-qty-${p.slug}`}>{p.stock}</p>
            {statusOf(p)}
            <div className="flex gap-2">
              <button data-testid={`entrada-${p.slug}`} onClick={() => { setModal({ type: "entrada", product: p }); setQty(""); setReason(""); }} className="flex items-center gap-1.5 rounded-full bg-military text-offwhite px-4 py-2 text-[10px] uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors"><ArrowDownToLine size={12} /> Entrada</button>
              <button data-testid={`saida-${p.slug}`} onClick={() => { setModal({ type: "saida", product: p }); setQty(""); setReason(""); }} className="flex items-center gap-1.5 rounded-full border border-military/25 text-military-dark px-4 py-2 text-[10px] uppercase tracking-widest font-semibold hover:bg-baby transition-colors"><ArrowUpFromLine size={12} /> Saída</button>
              <button data-testid={`ajuste-${p.slug}`} onClick={() => { setModal({ type: "ajuste", product: p }); setQty(String(p.stock)); setReason(""); }} className="flex items-center gap-1.5 rounded-full border border-military/25 text-military-dark px-4 py-2 text-[10px] uppercase tracking-widest font-semibold hover:bg-baby transition-colors"><SlidersHorizontal size={12} /> Ajuste</button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-2xl text-military-dark mb-4">Histórico de movimentações</h2>
      <div className="bg-white border border-military/10 rounded-lg overflow-x-auto" data-testid="movements-history">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-military-dark/50 border-b border-military/10">
              <th className="px-4 py-3">Produto</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Qtd</th><th className="px-4 py-3">Estoque após</th><th className="px-4 py-3">Data</th><th className="px-4 py-3">Responsável</th><th className="px-4 py-3">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {movements.map((m) => (
              <tr key={m.id} className="border-b border-military/5 last:border-0">
                <td className="px-4 py-3 font-medium text-military-dark">{m.product_name}</td>
                <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${typeColor[m.type] || "bg-military/10 text-military"}`}>{typeLabel[m.type] || m.type}</span></td>
                <td className="px-4 py-3">{m.qty}</td>
                <td className="px-4 py-3">{m.stock_after}</td>
                <td className="px-4 py-3 text-military-dark/60">{fmtDate(m.created_at)}</td>
                <td className="px-4 py-3 text-military-dark/60">{m.user}</td>
                <td className="px-4 py-3 text-military-dark/60">{m.reason || "—"}</td>
              </tr>
            ))}
            {movements.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-military-dark/40">Nenhuma movimentação registrada ainda.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-military-dark/40 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={() => setModal(null)}>
          <form data-testid="movement-form" onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-offwhite w-full md:max-w-md rounded-t-lg md:rounded-lg p-6 md:p-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif text-2xl text-military-dark capitalize">{typeLabel[modal.type]} de estoque</h2>
              <button type="button" onClick={() => setModal(null)} className="p-2 rounded-full hover:bg-baby"><X size={18} /></button>
            </div>
            <p className="text-sm text-military-dark/60 mb-6">{modal.product.name} — estoque atual: <strong>{modal.product.stock}</strong></p>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold text-military-dark/50 mb-1.5">
              {modal.type === "ajuste" ? "Nova quantidade em estoque" : "Quantidade"}
            </label>
            <input data-testid="movement-qty" required type="number" min={modal.type === "ajuste" ? 0 : 1} value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls + " mb-4"} />
            <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold text-military-dark/50 mb-1.5">Motivo / observação</label>
            <input data-testid="movement-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={modal.type === "entrada" ? "Ex.: reposição do fornecedor" : "Ex.: venda balcão, avaria..."} className={inputCls} />
            <button data-testid="movement-save" type="submit" className="mt-6 w-full rounded-full bg-military-dark text-offwhite py-3.5 text-sm uppercase tracking-widest font-semibold hover:bg-military transition-colors">
              Confirmar {typeLabel[modal.type].toLowerCase()}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
